import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("maze_admin_token", data.token);
      navigate("/admin/applications");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0D0E12] border border-white/15 rounded-sm px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-[#FF5500] transition-colors";

  return (
    <>
      <Seo title="Admin Login | Maze Security Solutions" noindex />
      <section className="max-w-md mx-auto px-4 py-24" data-testid="admin-login-page">
        <div className="border border-white/10 bg-[#181C28] rounded-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/favicon.png" alt="Maze Security Solutions logo" className="h-10 w-10" width="40" height="40" />
            <div>
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Admin Access</h1>
              <p className="text-xs text-gray-500 flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-[#FF5500]" /> Authorized personnel only</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="admin-email">Email</label>
              <input id="admin-email" type="email" required data-testid="admin-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="admin-password">Password</label>
              <input id="admin-password" type="password" required data-testid="admin-password-input" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {error && (
              <p className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-3 py-2 rounded-sm" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-sm transition-colors duration-200"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <p className="mt-4 text-xs text-gray-500 text-center">
            <Link to="/" data-testid="admin-back-home-link" className="hover:text-[#FF5500] transition-colors">← Back to site</Link>
          </p>
        </div>
      </section>
    </>
  );
}
